import { AttributionControl, Map } from 'mapbox-gl';
import { useEffect, useRef, useState } from 'react';

import useClick from './hooks/useClick';
import useClusters from './hooks/useClusters';
import useFeatureMirrors from './hooks/useFeatureMirrors';
import useImages from './hooks/useImages';
import useLayers from './hooks/useLayers';
import useMarkers from './hooks/useMarkers';

import {
    DEFAULT_CENTER,
    DEFAULT_ZOOM,
    MAP_CANVAS_TEST_ID,
    MAP_LAYER,
    MAP_STYLE,
    ON_CLICK_MARKER_EVENT,
    TEST_ID
} from './constants';
import type {
    GeoJSONFeature,
    MapBoxConfig,
    MapMouseEvent,
    OnClickMarkerEvent,
    Props
} from './types';

// Silence Mapbox's events.mapbox.com telemetry POST on style load. It 422s
// against our blob:-URL styles because the endpoint can't attribute a blob
// style back to a Studio source. Cosmetic console noise only; the map
// renders identically with the event queue disabled.
(Map.prototype as unknown as { _postStyleLoadEvent: () => void })._postStyleLoadEvent = () => {};

const DEFAULT_CONFIG: MapBoxConfig = {
    accessToken: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string,
    center: DEFAULT_CENTER,
    mapStyle: MAP_STYLE,
    zoom: DEFAULT_ZOOM
};

type TestMapContainer = HTMLDivElement & {
    __testMapboxMap?: Map;
};

const EMPTY_VERTEX_COORDS: NonNullable<Props['vertexCoords']> = [];

const MapBox = ({
    children,
    className = '',
    config = DEFAULT_CONFIG,
    geojson,
    markers,
    onClick,
    onDoubleClick,
    onError,
    onFeatureSelect,
    onLoad,
    onMouseMove,
    onMove,
    selectedFlightId,
    testId = TEST_ID,
    vertexCoords = EMPTY_VERTEX_COORDS
}: Props) => {
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const [map, setMap] = useState<Map>();
    const [isMapLoaded, setIsMapLoaded] = useState(false);

    const { handleClick } = useClick({
        onClick: () => onClick?.(),
        onDoubleClick: () => onDoubleClick?.()
    });
    const { drawFeatures, initializeLayers } = useLayers();
    const {
        handleClusterClick,
        handleUnclusteredClick,
        handleZoom,
        initializeZoomState,
        isClustered
    } = useClusters();
    const { updateMarkers } = useMarkers();
    const { loadImages } = useImages();

    drawFeatures(map, geojson);
    updateMarkers(map, markers, isClustered);
    useFeatureMirrors({ map, mapContainerRef, selectedFlightId, vertexCoords });

    useEffect(() => {
        if (!mapContainerRef?.current) {
            return;
        }

        const { accessToken, center, mapStyle, maxZoom, minZoom, zoom } = config;

        const newMap = new Map({
            accessToken,
            attributionControl: false,
            center: {
                lat: center[1],
                lng: center[0]
            },
            container: mapContainerRef.current,
            maxZoom,
            minZoom,
            style: mapStyle,
            zoom
        });
        const mapContainer = mapContainerRef.current as TestMapContainer;
        const mapCanvas = newMap.getCanvas();

        newMap.addControl(new AttributionControl(), 'top-right');
        newMap.dragRotate.disable();
        newMap.touchZoomRotate.disableRotation();

        newMap.once('load', () => {
            loadImages(newMap);

            newMap.on('styleimagemissing', () => {
                loadImages(newMap);
            });

            const { layerStyles } = config;
            const {
                getClusterStyle,
                getDrawingStyles,
                getStaticGeometryStyles,
                getUnclusteredStyle
            } = layerStyles || {};

            initializeLayers({
                clusterStyle: getClusterStyle?.(),
                drawingStyles: getDrawingStyles?.(),
                map: newMap,
                staticStyles: getStaticGeometryStyles?.(),
                unclusteredStyle: getUnclusteredStyle?.()
            });

            initializeZoomState(newMap);

            onMouseMove?.(DEFAULT_CENTER);
            onMove?.(DEFAULT_CENTER);
            onLoad?.(newMap);
            mapContainer.__testMapboxMap = newMap;
            mapCanvas.setAttribute('data-testid', MAP_CANVAS_TEST_ID);

            newMap.resize();

            setMap(newMap);
            setIsMapLoaded(true);
        });

        newMap.on('error', ({ error }) => {
            onError?.(error);
        });

        return () => {
            if (mapContainer.__testMapboxMap === newMap) {
                delete mapContainer.__testMapboxMap;
            }

            mapCanvas.removeAttribute('data-testid');
            newMap.remove();
            setMap(undefined);
            setIsMapLoaded(false);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapContainerRef]);

    useEffect(() => {
        if (!map) {
            return;
        }

        const selectFeatureById = (featureId?: string | number) => {
            const feature = geojson?.find(feature => feature.id === featureId);

            if (feature) {
                onFeatureSelect?.(feature);
            }
        };

        const handleClickMarker = (event: OnClickMarkerEvent) => {
            onClick?.(event.markerId);
            selectFeatureById(event.markerFeatureId);
        };

        const handleStaticFeatureClick = (
            event: MapMouseEvent & { features?: GeoJSONFeature[] }
        ) => {
            selectFeatureById(event.features?.[0]?.id);
        };

        const handleMove = () => {
            const { lat, lng } = map.getCenter();

            onMove?.([lng, lat]);
        };

        const handleMouseMove = (event: MapMouseEvent) => {
            const { lat, lng } = event.lngLat;

            onMouseMove?.([lng, lat]);
        };

        map.on(ON_CLICK_MARKER_EVENT, handleClickMarker);
        map.on('click', handleClick);
        map.on('move', handleMove);
        map.on('mousemove', handleMouseMove);
        map.on('zoom', handleZoom);
        map.on('zoomend', handleZoom);
        map.on('click', MAP_LAYER.CLUSTER, handleClusterClick);
        map.on('click', MAP_LAYER.UNCLUSTERED_POINT, handleUnclusteredClick);
        map.on('click', MAP_LAYER.STATIC_BOUNDARY_FILL, handleStaticFeatureClick);
        map.on('click', MAP_LAYER.STATIC_OBSTACLE_FILL, handleStaticFeatureClick);

        return () => {
            map.off(ON_CLICK_MARKER_EVENT, handleClickMarker);
            map.off('click', handleClick);
            map.off('move', handleMove);
            map.off('mousemove', handleMouseMove);
            map.off('zoom', handleZoom);
            map.off('zoomend', handleZoom);
            map.off('click', MAP_LAYER.CLUSTER, handleClusterClick);
            map.off('click', MAP_LAYER.UNCLUSTERED_POINT, handleUnclusteredClick);
            map.off('click', MAP_LAYER.STATIC_BOUNDARY_FILL, handleStaticFeatureClick);
            map.off('click', MAP_LAYER.STATIC_OBSTACLE_FILL, handleStaticFeatureClick);
        };
    }, [
        geojson,
        map,
        onClick,
        onFeatureSelect,
        onMove,
        onMouseMove,
        handleClick,
        handleClusterClick,
        handleUnclusteredClick,
        handleZoom
    ]);

    return (
        <div
            className={className}
            data-map-loaded={isMapLoaded ? 'true' : undefined}
            data-testid={testId}
            ref={mapContainerRef}>
            {children}
        </div>
    );
};

export default MapBox;
